import {
  AddHollow,
  Checkmark,
  HubspotServices,
  PeopleNav,
  RemoveHollow,
  SalesforceServices,
} from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKActionMenu,
  CKButton,
  CKLayoutHorizontal,
  CKPopover,
} from 'clearkit';
import { useEffect, useRef } from 'react';

import { ButtonContainer } from '~/components/unified/ButtonContainer';
import { PageLayout } from '~/components/unified/PageLayout';

import { hideListSmall } from './constants';

type ListFooterProps = {
  onSelectAll: () => void;
  onSelectAllPage: () => void;
  onDeselectAll: () => void;
  selectAllChecked: boolean;
  selectedProspectsCount: number;
  totalProspects: number;
};

export const ListFooter = ({
  onSelectAll,
  selectAllChecked,
  onSelectAllPage,
  onDeselectAll,
  selectedProspectsCount,
  totalProspects,
}: ListFooterProps) => {
  const SelectAllMenu = () => (
    <CKActionMenu>
      <CKActionMenu.Item leftIcon={<AddHollow />} onClick={onSelectAllPage}>
        Select all prospects on this page
      </CKActionMenu.Item>
      <CKActionMenu.Item leftIcon={<AddHollow />} onClick={onSelectAll}>
        Select all{' '}
        <strong className="font-medium">
          {totalProspects.toLocaleString('en-US')}
        </strong>{' '}
        prospects
      </CKActionMenu.Item>
      <CKActionMenu.Divider />
      <CKActionMenu.Item
        isDisabled={!selectAllChecked}
        leftIcon={<RemoveHollow />}
        onClick={onDeselectAll}
      >
        Deselect all
      </CKActionMenu.Item>
    </CKActionMenu>
  );

  const SelectAll = () => {
    const checkboxRef = useRef<HTMLInputElement>(null);

    const isIndeterminate =
      (selectAllChecked || !!selectedProspectsCount) &&
      selectedProspectsCount < totalProspects;

    useEffect(() => {
      if (isIndeterminate && checkboxRef.current) {
        checkboxRef.current.indeterminate = isIndeterminate;
      }
    }, []);

    return (
      <CKPopover popover={<SelectAllMenu />} popoverWidth="22rem">
        <CKPopover.Trigger className="!px-3">
          <span className="ck-checkbox">
            <input
              className="sr-only !absolute ck-checkbox-input"
              ref={checkboxRef}
              type="checkbox"
            />
            <span className={classnames('ck-checkbox-label')}>
              <span
                className={classnames('ck-checkbox-box -mt-1.5', {
                  'ck-checkbox-box--selected':
                    selectAllChecked || !!selectedProspectsCount,
                })}
              >
                {!isIndeterminate ? <Checkmark height="14" width="14" /> : null}
              </span>
            </span>
          </span>
        </CKPopover.Trigger>
      </CKPopover>
    );
  };

  const AddToMenu = () => (
    <CKActionMenu>
      <CKActionMenu.Item leftIcon={<SalesforceServices />}>
        Salesforce
      </CKActionMenu.Item>
      <CKActionMenu.Item leftIcon={<HubspotServices />}>
        Hubspot
      </CKActionMenu.Item>
    </CKActionMenu>
  );

  return (
    <PageLayout.Footer>
      <CKLayoutHorizontal className="text-sm" gap=".5rem">
        <CKLayoutHorizontal>
          <SelectAll />
          <CKLayoutHorizontal className="text-sm" gap=".5rem">
            <PeopleNav height={18} width={18} />
            <span>
              {selectedProspectsCount?.toLocaleString('en-US')}{' '}
              <span className={hideListSmall}>
                {selectedProspectsCount === 1 ? 'prospect' : 'prospects'}
              </span>{' '}
              selected
            </span>
          </CKLayoutHorizontal>
        </CKLayoutHorizontal>
        <ButtonContainer>
          <CKButton size="small" variantColor="blue">
            Download to CSV
          </CKButton>
          <CKPopover popover={<AddToMenu />}>
            <CKPopover.Trigger size="small" variantColor="blue">
              Add prospects to...
            </CKPopover.Trigger>
          </CKPopover>
        </ButtonContainer>
      </CKLayoutHorizontal>
    </PageLayout.Footer>
  );
};
